import { ref } from '@vue/composition-api'
import analytics from '@/@core/utils/analytics'
import { apiToastSuccessV2, apiToastErrorV2 } from '@/@core/utils/toast'
import constants, { FLAG_CONSTANTS } from '@/constants'
import store from '@/store'
import { SHARE_LINK_WISHLIST } from '@/store/modules/shop.module'
import { sendLineSheetOutreachToParent } from '@/service/iframe-auth.service'

export function useShareLinkSelection(wishlistIdRef, wishlistNameRef) {
  const loading = ref(false)
  const shareLink = ref('')
  const error = ref(null)
  const isCopied = ref(false)

  async function getLink() {
    loading.value = true
    try {
      const response = await store.dispatch(SHARE_LINK_WISHLIST, {
        payload: { isActive: true },
        wishlistId: wishlistIdRef.value,
      })
      processResponse(response)
      analytics.track(constants.TRACKS.ACTIONS.GET_SHARE_LINK_SUCCESS)
    } catch (err) {
      handleError(err)
      analytics.track(constants.TRACKS.ACTIONS.GET_SHARE_LINK_FAILURE)
    } finally {
      loading.value = false
    }
  }

  function processResponse(response) {
    const { data } = response
    if (data?.response?.publicToken) {
      const domain = `${window.location.protocol}//${window.location.host}`
      // KP-3289 flag ON → new V2 (glassmorphic) public page; OFF → legacy page.
      const isV2 = store.getters['flags/hasFeature'](FLAG_CONSTANTS.PUBLIC_WISHLIST_V2)
      const path = isV2 ? '/v2/wishlist/public/' : '/wishlist/public/'
      shareLink.value = `${domain}${path}${data.response.publicToken}`
      isCopied.value = false
      error.value = null
    } else {
      throw new Error('Invalid response from API')
    }
  }

  function handleError(err) {
    console.error(err)
    error.value = err
  }

  function copyLink() {
    navigator.clipboard.writeText(shareLink.value)
    analytics.track(constants.TRACKS.ACTIONS.COPY_LINK_BUTTON_CLICKED)
    apiToastSuccessV2('Link copied to clipboard')
    isCopied.value = true
    setTimeout(() => {
      isCopied.value = false
    }, 4000)
  }

  /**
   * Hands the public link off to the PLG parent app (KP-3326, Path 4) to use in
   * a sequence or template. `destination` is 'sequence' | 'template'; `mode` is
   * 'new' | 'existing'. PLG routes to its drafting flow with the link attached.
   */
  function useInOutreach(destination, mode) {
    if (!shareLink.value) return false
    const sent = sendLineSheetOutreachToParent({
      lineSheetUrl: shareLink.value,
      lineSheetName: wishlistNameRef?.value,
      destination,
      mode,
    })
    if (!sent) {
      apiToastErrorV2('Couldn’t open this in your outreach. Please try again.')
      return false
    }
    analytics.track(constants.TRACKS.ACTIONS.USE_WISHLIST_IN_OUTREACH, { destination, mode })
    apiToastSuccessV2('Opening in your outreach…')
    return true
  }

  return {
    loading,
    shareLink,
    error,
    isCopied,
    getLink,
    copyLink,
    useInOutreach,
  }
}
